import { useEffect, useState } from "react";
import { Button } from "../../../components/ui/button";
import Common from "../../../helper/common";
import {
  respEnum,
  TournamentMatchStageEnum,
  TournamentRuleMatchFormatTypeEnum,
} from "../../../constants/appConstant";
import { AppInput, AppSelect } from "../../../components/appComponents";
import Card from "../../../components/ui/card-snippet";
import { useLocation } from "react-router-dom";
import { useQueryParams } from "../../../contexts/queryParamsContext";

const TimeInput = ({ minutes, seconds, onChange, label, required }) => {
  const handleTimeChange = (e) => {
    const timeValue = e.target.value;
    const timeParts = timeValue.split(":");
    const mins =
      timeParts.length > 1 ? parseInt(timeParts[timeParts.length - 2]) : 0;
    const secs =
      timeParts.length > 0 ? parseInt(timeParts[timeParts.length - 1]) : 0;

    onChange({
      minutes: Math.min(mins || 0, 59),
      seconds: Math.min(secs || 0, 59),
    });
  };

  const formatTime = (min, sec) => {
    if (min === null && sec === null) return "";
    return `${String(Math.min(min || 0, 59)).padStart(2, "0")}:${String(
      Math.min(sec || 0, 59)
    ).padStart(2, "0")}`;
  };

  const handleClear = () => {
    onChange({ minutes: null, seconds: null });
  };

  return (
    <div>
      <label className="block text-sm font-medium mb-1">{label}</label>
      <div className="relative">
        <input
          type="text"
          required={required}
          value={formatTime(minutes, seconds)}
          onChange={handleTimeChange}
          className="w-full rounded-md border border-gray-300 px-3 py-2 pr-8"
          placeholder="mm:ss"
          maxLength={5}
          onKeyPress={(e) => {
            if (!/[\d:]/.test(e.key)) {
              e.preventDefault();
            }
            if (e.key === ":" && e.target.value.includes(":")) {
              e.preventDefault();
            }
          }}
          onBlur={(e) => {
            if (!e.target.value) {
              onChange({ minutes: null, seconds: null });
            } else {
              const [mins, secs] = e.target.value.split(":").map(Number);
              e.target.value = formatTime(mins, secs);
            }
          }}
        />
        {(minutes !== null || seconds !== null) && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
};

const validateNumberInput = (value) => {
  if (!value && value !== 0) return 0;
  const num = parseInt(value);
  if (isNaN(num)) return 0;
  if (num < 0) return 0;
  if (num > 255) return 255;
  return num;
};

export default function Rules() {
  const [rules, setRules] = useState({});
  const [saveBtnDisabled, setSaveBtnDisabled] = useState({});

  const [tournamentId, setTournamentId] = useState(null);
  const { params, setParams } = useQueryParams();

  useEffect(() => {
    initialize();

    return () => {
      setParams({
        ...params,
        tournamentId: null,
      });
    };
  }, []);

  const initialize = async () => {
    const fetchedId = params.tournamentId;
    if (fetchedId) {
      setTournamentId(fetchedId);
      await fetchRules(fetchedId);
    }
  };

  const fetchRules = async (id) => {
    const response = await Common.ApiService.getInstance().request(
      `GetTournamentRules?tournamentId=${id}`
    );

    if (response?.data) {
      const groupedRules = {};
      Object.keys(TournamentMatchStageEnum).forEach((stage) => {
        const stageRule = response.data.find(
          (r) => r.stageType === TournamentMatchStageEnum[stage]
        );
        if (stageRule) {
          groupedRules[stage] = {
            id: stageRule.id,
            tournamentId: id,
            stageType: stageRule.stageType,
            autoStartTime: stageRule.autoStartTime,
            pointTimerMinutes: stageRule.pointTimerMinutes,
            pointTimerSeconds: stageRule.pointTimerSeconds,
            changeSide1_Minutes: stageRule.changeSide1_Minutes,
            changeSide1_Seconds: stageRule.changeSide1_Seconds,
            changeSideMinutes: stageRule.changeSideMinutes,
            changeSideSeconds: stageRule.changeSideSeconds,
            setTimerMinutes: stageRule.setTimerMinutes,
            setTimerSeconds: stageRule.setTimerSeconds,
            numberOfSets: stageRule.numberOfSets,
            numberOfGames: stageRule.numberOfGames,
            autoChangeSide: stageRule.autoChangeSide,
            goldenPoint: stageRule.goldenPoint,
            goldenPointInTiebreak: stageRule.goldenPointInTiebreak,
            advantagesWithGoldenPoint: stageRule.advantagesWithGoldenPoint,
            tiebreakOnLastSet: stageRule.tiebreakOnLastSet,
            pointsInTiebreak: stageRule.pointsInTiebreak,
            gamesToStartTiebreak: stageRule.gamesToStartTiebreak,
            matchFormat:
              stageRule.matchFormat ||
              TournamentRuleMatchFormatTypeEnum.ThreeSets,
            superTieBreakPoints: stageRule.superTieBreakPoints || 10,
          };
        } else {
          groupedRules[stage] = createDefaultRule(
            TournamentMatchStageEnum[stage]
          );
        }
      });

      setRules(groupedRules);
    }
  };

  const createDefaultRule = (stageType) => ({
    tournamentId: tournamentId,
    stageType: stageType,
    autoStartTime: true,
    pointTimerMinutes: 0,
    pointTimerSeconds: 20,
    changeSide1_Minutes: 1,
    changeSide1_Seconds: 30,
    changeSideMinutes: 1,
    changeSideSeconds: 30,
    setTimerMinutes: 2,
    setTimerSeconds: 0,
    numberOfSets: 3,
    numberOfGames: 6,
    autoChangeSide: true,
    goldenPoint: true,
    goldenPointInTiebreak: false,
    advantagesWithGoldenPoint: 0,
    tiebreakOnLastSet: true,
    pointsInTiebreak: 7,
    gamesToStartTiebreak: "6-6",
    matchFormat: TournamentRuleMatchFormatTypeEnum.ThreeSets,
    superTieBreakPoints: 10,
  });

  const handleRuleChange = (stage, field, value) => {
    const fieldsToValidate = [
      "numberOfSets",
      "numberOfGames",
      "pointsInTiebreak",
      "advantagesWithGoldenPoint",
      "superTieBreakPoints",
    ];
    setRules((prev) => ({
      ...prev,
      [stage]: {
        ...prev[stage],
        [field]: value,
      },
    }));
  };

  const handleTimeChange = (stage, minField, secField, value) => {
    setRules((prev) => ({
      ...prev,
      [stage]: {
        ...prev[stage],
        [minField]: value.minutes,
        [secField]: value.seconds,
      },
    }));
  };

  const generateTiebreakOptions = (numberOfGames) => {
    const options = [];
    const num = parseInt(numberOfGames);
    if (num > 0) {
      options.push({ id: `${num}-${num}`, name: `${num}-${num}` });
      if (num > 1) {
        options.push({
          id: `${num - 1}-${num - 1}`,
          name: `${num - 1}-${num - 1}`,
        });
      }
    }
    return [{ id: "0", name: "Select Games" }, ...options];
  };

  const generateMatchFormatOptions = () => {
    return Object.keys(TournamentRuleMatchFormatTypeEnum).map((key) => ({
      id: TournamentRuleMatchFormatTypeEnum[key],
      name: key.replace(/([A-Z])/g, " $1").trim(), // Convert camelCase to readable format
    }));
  };

  const handleNumberOfGamesChange = (stage, value) => {
    setRules((prev) => ({
      ...prev,
      [stage]: {
        ...prev[stage],
        numberOfGames: value,
        gamesToStartTiebreak: "", // Reset tiebreak value
      },
    }));
  };

  const handleSave = async (stage) => {
    setSaveBtnDisabled((prev) => ({ ...prev, [stage]: true }));

    try {
      const ruleData = rules[stage];

      const response = await Common.ApiService.getInstance().request(
        "AddUpdateTournamentRule",
        {
          id: ruleData.id || 0,
          tournamentId: tournamentId,
          stageType: ruleData.stageType,
          autoStartTime: ruleData.autoStartTime,
          pointTimerMinutes: ruleData.pointTimerMinutes,
          pointTimerSeconds: ruleData.pointTimerSeconds,
          changeSide1_Minutes: ruleData.changeSide1_Minutes,
          changeSide1_Seconds: ruleData.changeSide1_Seconds,
          changeSideMinutes: ruleData.changeSideMinutes,
          changeSideSeconds: ruleData.changeSideSeconds,
          setTimerMinutes: ruleData.setTimerMinutes,
          setTimerSeconds: ruleData.setTimerSeconds,
          numberOfSets: ruleData.numberOfSets,
          numberOfGames: ruleData.numberOfGames,
          autoChangeSide: ruleData.autoChangeSide,
          goldenPoint: ruleData.goldenPoint,
          goldenPointInTiebreak: ruleData.goldenPointInTiebreak,
          advantagesWithGoldenPoint: ruleData.advantagesWithGoldenPoint,
          tiebreakOnLastSet: ruleData.tiebreakOnLastSet,
          pointsInTiebreak: ruleData.pointsInTiebreak,
          gamesToStartTiebreak: ruleData.gamesToStartTiebreak,
          matchFormat: ruleData.matchFormat,
          superTieBreakPoints: ruleData.superTieBreakPoints,
        },
        "POST"
      );

      if (response.status === respEnum.Success) {
        Common.showToast(response.message, "Success", "success");
        await fetchRules(tournamentId);
      }
    } catch (error) {
      Common.showToast("Error saving rules", "Error");
    } finally {
      setSaveBtnDisabled((prev) => ({ ...prev, [stage]: false }));
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {Object.keys(TournamentMatchStageEnum).map((stage) => (
        <Card key={stage}>
          <div className="text-center mb-4">
            <h2 className="text-xl font-bold text-gray-800 border-b-2 border-gray-200 pb-2">
              {stage.replace(/_/g, " ")}
            </h2>
          </div>

          <div className="space-y-4">
            {/* Match Settings Section */}
            <div className="border rounded-lg p-3 shadow-md hover:shadow-lg transition-shadow duration-300 bg-white">
              <h3 className="text-md font-semibold text-center mb-3 text-gray-700 border-b pb-2">
                Match Settings
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                <AppInput
                  type="number"
                  value={rules[stage]?.numberOfSets}
                  label="Number of Sets *"
                  required={true}
                  min={0}
                  max={255}
                  onChange={(val) =>
                    handleRuleChange(
                      stage,
                      "numberOfSets",
                      validateNumberInput(val)
                    )
                  }
                />
                <AppInput
                  type="number"
                  value={rules[stage]?.numberOfGames}
                  label="Number of Games *"
                  required={true}
                  min={0}
                  max={255}
                  onChange={(val) => handleNumberOfGamesChange(stage, val)}
                />
                <AppSelect
                  value={rules[stage]?.matchFormat}
                  label="Match Format"
                  data={generateMatchFormatOptions()}
                  onChange={(val) =>
                    handleRuleChange(stage, "matchFormat", val)
                  }
                />
                <AppInput
                  type="number"
                  value={rules[stage]?.superTieBreakPoints}
                  label="Super Tie Break Points"
                  min={0}
                  max={255}
                  onChange={(val) =>
                    handleRuleChange(
                      stage,
                      "superTieBreakPoints",
                      validateNumberInput(val)
                    )
                  }
                />
                <AppSelect
                  value={rules[stage]?.autoStartTime}
                  label="Auto Start Time"
                  data={[
                    { id: true, name: "Yes" },
                    { id: false, name: "No" },
                  ]}
                  onChange={(val) =>
                    handleRuleChange(stage, "autoStartTime", val)
                  }
                />
                <AppSelect
                  value={rules[stage]?.autoChangeSide}
                  label="Auto Change Side"
                  data={[
                    { id: true, name: "Yes" },
                    { id: false, name: "No" },
                  ]}
                  onChange={(val) =>
                    handleRuleChange(stage, "autoChangeSide", val)
                  }
                />
              </div>
            </div>

            {/* Timer Settings */}
            <div className="border rounded-lg p-3 shadow-md hover:shadow-lg transition-shadow duration-300 bg-white">
              <h3 className="text-md font-semibold text-center mb-3 text-gray-700 border-b pb-2">
                Timer Settings
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                <TimeInput
                  minutes={rules[stage]?.pointTimerMinutes}
                  seconds={rules[stage]?.pointTimerSeconds}
                  label="Point Timer"
                  onChange={(value) =>
                    handleTimeChange(
                      stage,
                      "pointTimerMinutes",
                      "pointTimerSeconds",
                      value
                    )
                  }
                />
                <TimeInput
                  minutes={rules[stage]?.setTimerMinutes}
                  seconds={rules[stage]?.setTimerSeconds}
                  label="Set Timer"
                  onChange={(value) =>
                    handleTimeChange(
                      stage,
                      "setTimerMinutes",
                      "setTimerSeconds",
                      value
                    )
                  }
                />
                <TimeInput
                  minutes={rules[stage]?.changeSideMinutes}
                  seconds={rules[stage]?.changeSideSeconds}
                  label="Change Side Timer"
                  onChange={(value) =>
                    handleTimeChange(
                      stage,
                      "changeSideMinutes",
                      "changeSideSeconds",
                      value
                    )
                  }
                />
                <TimeInput
                  minutes={rules[stage]?.changeSide1_Minutes}
                  seconds={rules[stage]?.changeSide1_Seconds}
                  label="Change Side 1 Timer"
                  onChange={(value) =>
                    handleTimeChange(
                      stage,
                      "changeSide1_Minutes",
                      "changeSide1_Seconds",
                      value
                    )
                  }
                />
              </div>
            </div>

            {/* Tiebreak Settings */}
            <div className="border rounded-lg p-3 shadow-md hover:shadow-lg transition-shadow duration-300 bg-white">
              <h3 className="text-md font-semibold text-center mb-3 text-gray-700 border-b pb-2">
                Tiebreak Settings
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                <AppInput
                  type="number"
                  value={rules[stage]?.pointsInTiebreak}
                  label="Points in Tiebreak"
                  min={0}
                  max={255}
                  onChange={(val) =>
                    handleRuleChange(stage, "pointsInTiebreak", val)
                  }
                />
                <AppSelect
                  value={rules[stage]?.gamesToStartTiebreak || "0"}
                  label="Games to Start Tiebreak"
                  data={generateTiebreakOptions(rules[stage]?.numberOfGames)}
                  onChange={(val) =>
                    handleRuleChange(
                      stage,
                      "gamesToStartTiebreak",
                      val === "0" ? "" : val
                    )
                  }
                />
                <AppSelect
                  value={rules[stage]?.tiebreakOnLastSet}
                  label="Tiebreak on Last Set"
                  data={[
                    { id: true, name: "Yes" },
                    { id: false, name: "No" },
                  ]}
                  onChange={(val) =>
                    handleRuleChange(stage, "tiebreakOnLastSet", val)
                  }
                />
                <AppSelect
                  value={rules[stage]?.goldenPointInTiebreak}
                  label="Golden Point in Tiebreak"
                  data={[
                    { id: true, name: "Yes" },
                    { id: false, name: "No" },
                  ]}
                  onChange={(val) =>
                    handleRuleChange(stage, "goldenPointInTiebreak", val)
                  }
                />
              </div>
            </div>

            {/* Golden Point Settings */}
            <div className="border rounded-lg p-3 shadow-md hover:shadow-lg transition-shadow duration-300 bg-white">
              <h3 className="text-md font-semibold text-center mb-3 text-gray-700 border-b pb-2">
                Golden Point Settings
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                <AppSelect
                  value={rules[stage]?.goldenPoint}
                  label="Golden Point"
                  data={[
                    { id: true, name: "Yes" },
                    { id: false, name: "No" },
                  ]}
                  onChange={(val) =>
                    handleRuleChange(stage, "goldenPoint", val)
                  }
                />
                <AppInput
                  type="number"
                  value={rules[stage]?.advantagesWithGoldenPoint}
                  label="Advantages with Golden Point"
                  onChange={(val) =>
                    handleRuleChange(stage, "advantagesWithGoldenPoint", val)
                  }
                />
              </div>
            </div>

            <div className="flex justify-center mt-4">
              <Button
                onClick={() => handleSave(stage)}
                disabled={
                  saveBtnDisabled[stage] ||
                  !rules[stage]?.numberOfSets ||
                  !rules[stage]?.numberOfGames
                }
                className="px-6 py-2 shadow-sm hover:shadow-md transition-shadow duration-300"
              >
                {saveBtnDisabled[stage] ? "Updating..." : "Update Rule"}
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
